import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  User, Mail, Lock, Phone, ArrowRight, ArrowLeft,
  Briefcase, Eye, EyeOff, Loader2, Globe, CheckCircle2,
  AlertCircle
} from 'lucide-react';
import './CareersAuth.css';

const CareersAuth = () => {
  const [view, setView] = useState('login');
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const navigate = useNavigate();

  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth - 0.5) * 2,
        y: (e.clientY / window.innerHeight - 0.5) * 2,
      });
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    setSuccessMsg('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMsg('');

    try {
      // 1. FORGOT PASSWORD
      if (view === 'forgotPassword') {
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/careers-forgot-password`, {
          email: formData.email
        });
        setSuccessMsg("If an account exists, a reset link has been sent to your email!");
        setLoading(false);
        return;
      }

      // 2. REGISTER
      if (view === 'register') {
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/careers-register`, {
          fullName: formData.fullName,
          phone: formData.phone,
          email: formData.email,
          password: formData.password
        });
        setSuccessMsg("Account created successfully! Please log in.");
        setView('login');
        setFormData(prev => ({ ...prev, password: '' }));
        setLoading(false);
        return;
      }

      // 3. LOGIN
      if (view === 'login') {
        const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/careers-login`, {
          email: formData.email,
          password: formData.password
        });
        localStorage.setItem('applicantId', response.data.applicantId);
        localStorage.setItem('applicantName', response.data.fullName);
        localStorage.setItem('applicantEmail', response.data.email);
        
        // --- SMART ROUTING LOGIC ---
        const pendingJobId = localStorage.getItem('pendingApplyJobId');
        if (pendingJobId) {
          localStorage.removeItem('pendingApplyJobId'); // Clean up
          navigate(`/careers-dashboard?jobId=${pendingJobId}`); // Send with Job ID
        } else {
          navigate('/careers-dashboard'); // Normal flow
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const switchView = (newView) => {
    setView(newView);
    setError('');
    setSuccessMsg('');
  };

  const formatTime = (date) =>
    date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });

  const formatDate = (date) =>
    date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  const getMeta = () => {
    if (view === 'login') return { title: 'Welcome Back', sub: 'Log in to your careers dashboard' };
    if (view === 'register') return { title: 'Create Account', sub: 'Discover global hospitality opportunities' };
    return { title: 'Reset Password', sub: 'Enter your email to receive a reset link' };
  };
  const meta = getMeta();

  return (
    <div className="car-auth">
      {/* Background */}
      <div
        className="car-bg-image"
        style={{ transform: `translate3d(${mousePos.x * -10}px, ${mousePos.y * -6}px, 0) scale(1.05)` }}
      />
      <div className="car-bg-overlay" />
      <div className="car-bg-vignette" />
      <div
        className="car-bg-mesh car-mesh-1"
        style={{ transform: `translate3d(${mousePos.x * 20}px, ${mousePos.y * 20}px, 0)` }}
      />
      <div
        className="car-bg-mesh car-mesh-2"
        style={{ transform: `translate3d(${mousePos.x * -18}px, ${mousePos.y * -18}px, 0)` }}
      />

      {/* Top bar with back button */}
      <div className="car-top-bar">
        <button
          type="button"
          className="car-back-home"
          onClick={() => navigate('/')}
        >
          <ArrowLeft size={14} />
          <span>Back to Home</span>
        </button>

        <div className="car-top-meta">
          <span className="car-status">
            <span className="car-pulse-dot" />
            <span>SECURE SESSION</span>
          </span>
          <span className="car-dot">·</span>
          <span className="car-time">{formatTime(currentTime)}</span>
          <span className="car-dot">·</span>
          <span>{formatDate(currentTime)}</span>
          <span className="car-dot">·</span>
          <span className="car-region"><Globe size={11} /> DXB</span>
        </div>
      </div>

      {/* Content */}
      <div className="car-content">
        {/* Brand */}
        <div className="car-brand-col">
          <div className="car-brand-mark">
            <div className="car-brand-icon">
              <Briefcase size={20} strokeWidth={2} />
            </div>
            <div className="car-brand-text">
              <span className="car-brand-name">HOTELPLACEMENT</span>
              <span className="car-brand-sub">Careers Portal</span>
            </div>
          </div>

          <h1 className="car-headline">
            Step Into Your <br />
            <span className="car-gold-text">Next Opportunity</span>
          </h1>

          <p className="car-subtext">
            Apply directly to premium hospitality roles across the globe.
            Track your applications, manage your profile, and grow your career.
          </p>

          <div className="car-foot-meta">
            <div className="car-foot-item">
              <CheckCircle2 size={13} />
              <span>Direct applications to verified employers</span>
            </div>
            <div className="car-foot-item">
              <Lock size={13} />
              <span>End-to-end encryption</span>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="car-form-col">
          <div className="car-panel">
            <div className="car-panel-accent" />
            <div className="car-panel-corner car-c-tl" />
            <div className="car-panel-corner car-c-tr" />
            <div className="car-panel-corner car-c-bl" />
            <div className="car-panel-corner car-c-br" />

            <div className="car-security-badge">
              <Lock size={10} />
              <span>TLS 1.3 SECURE</span>
            </div>

            <div className="car-panel-header">
              <h2>{meta.title}</h2>
              <p>{meta.sub}</p>
            </div>

            {/* Alerts */}
            {error && (
              <div className="car-alert car-alert-error">
                <AlertCircle size={14} />
                <span>{error}</span>
              </div>
            )}
            {successMsg && (
              <div className="car-alert car-alert-success">
                <CheckCircle2 size={14} />
                <span>{successMsg}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="car-form">
              {view === 'register' && (
                <>
                  <div className="car-field car-slide">
                    <label>Full Name</label>
                    <div className="car-input-wrap">
                      <User className="car-input-icon" size={16} />
                      <input
                        required
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        placeholder="Your full name"
                      />
                    </div>
                  </div>

                  <div className="car-field car-slide" style={{ animationDelay: '60ms' }}>
                    <label>Phone Number</label>
                    <div className="car-input-wrap">
                      <Phone className="car-input-icon" size={16} />
                      <input
                        required
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91 98765 43210"
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="car-field">
                <label>Email Address</label>
                <div className="car-input-wrap">
                  <Mail className="car-input-icon" size={16} />
                  <input
                    required
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              {view !== 'forgotPassword' && (
                <div className="car-field">
                  <label>Password</label>
                  <div className="car-input-wrap">
                    <Lock className="car-input-icon" size={16} />
                    <input
                      required
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="••••••••••••"
                    />
                    <button
                      type="button"
                      className="car-eye-btn"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
              )}

              {view === 'login' && (
                <div className="car-form-extras">
                  <button
                    type="button"
                    className="car-forgot-link"
                    onClick={() => switchView('forgotPassword')}
                  >
                    Forgot Password?
                  </button>
                </div>
              )}

              <button type="submit" className="car-submit" disabled={loading}>
                <span className="car-submit-bg" />
                <span className="car-submit-content">
                  {loading ? (
                    <><Loader2 className="car-spin" size={17} /><span>Processing...</span></>
                  ) : (
                    <>
                      <span>
                        {view === 'login' ? 'Log In' : view === 'register' ? 'Create Account' : 'Send Reset Link'}
                      </span>
                      <ArrowRight size={15} className="car-submit-arrow" />
                    </>
                  )}
                </span>
              </button>

              {/* Bottom switch */}
              {view === 'forgotPassword' ? (
                <button
                  type="button"
                  className="car-back-link"
                  onClick={() => switchView('login')}
                >
                  <ArrowLeft size={14} /> <span>Back to <strong>Login</strong></span>
                </button>
              ) : (
                <div className="car-toggle-row">
                  <span>{view === 'login' ? "Don't have an account?" : "Already have an account?"}</span>
                  <button
                    type="button"
                    className="car-toggle-link"
                    onClick={() => switchView(view === 'login' ? 'register' : 'login')}
                  >
                    {view === 'login' ? 'Sign up' : 'Log in'}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CareersAuth;